import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router'
import { track } from '@/lib/analytics'
import { emitPollEvent } from '@/api/pollEvents'
import { fireCelebration } from '@/lib/confetti'
import { PollsWizardShell } from '@/onboarding/PollsWizardShell'
import { usePollsWizard } from '@/onboarding/PollsWizardContext'

/**
 * Slide 20 · «Опрос уже в канале» — шаг 3/3.
 * Success screen. Только [К опросам] (Виктор 2026-07-27: у опросов нет тарифов).
 * После рендера reset()'им context — если юзер вернётся ещё раз, state чистый.
 */
export function PollsWizardSuccessPage() {
  const [params] = useSearchParams()
  const { state, reset } = usePollsWizard()
  const pollId = params.get('pollId')
  const ref = params.get('ref') ?? state.ref

  useEffect(() => {
    fireCelebration()
    track('poll_wizard_success', { pollId, ref })
    if (pollId) {
      emitPollEvent('poll_onboarding_step_completed', {
        pollId,
        meta: {
          source: 'lk',
          step: 'published',
          ref: ref ?? null,
          channelUUID: state.channelUUID ?? null,
          template: state.templateId,
        },
      })
    }
    // Reset — при новом заходе wizard'а state чистый.
    return () => reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PollsWizardShell step={3} total={3} showCancel={false}>
      <div className="pw-success">
        <div className="pw-success-icon" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <p className="pw-eyebrow-line">
          <span className="pw-eyebrow-pill pw-eyebrow-pill--ok">Опубликовано</span>
        </p>
        <h1 className="pw-title">
          Опрос уже <em>в&nbsp;канале</em>.
        </h1>
        <p className="pw-sub">
          Первые голоса появятся за&nbsp;минуты. Результаты и&nbsp;сегменты — кто&nbsp;пришёл с&nbsp;рекламы,
          кто&nbsp;из&nbsp;ядра — смотрите в&nbsp;разделе «Опросы».
        </p>
        <div className="pw-actions">
          <Link to={pollId ? `/polls/${pollId}` : '/polls'} className="pw-btn pw-btn-primary">
            К&nbsp;опросам →
          </Link>
        </div>
      </div>
      <PageStyles />
    </PollsWizardShell>
  )
}

function PageStyles() {
  return (
    <style>{`
      .pw-success {
        max-width: 560px; margin: 40px auto 0;
        text-align: center;
      }
      .pw-success-icon {
        width: 68px; height: 68px; margin: 0 auto 20px;
        border-radius: 50%;
        background: rgba(23,160,106,0.10); color: var(--tg-up, #17a06a);
        display: flex; align-items: center; justify-content: center;
      }
      .pw-success-icon svg { width: 32px; height: 32px; }
      .pw-eyebrow-line { display: flex; justify-content: center; margin: 0 0 14px; }
      .pw-eyebrow-pill--ok {
        background: rgba(23,160,106,0.10) !important;
        color: var(--tg-up, #17a06a) !important;
      }
      .pw-eyebrow-pill {
        display: inline-flex; padding: 4px 10px; border-radius: 999px;
        background: var(--tg-blue-bg, #ebf2ff); color: var(--tg-blue-press, #0052cc);
        font-weight: 700; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
      }
      .pw-title {
        font-weight: 800; font-size: 36px; line-height: 1.05;
        letter-spacing: -0.026em; color: var(--tg-ink, #111);
        margin: 0 0 10px;
      }
      .pw-title em { font-style: italic; font-weight: 400; color: var(--tg-blue, #0066ff); }
      .pw-sub {
        font-size: 14.5px; color: var(--tg-mute, #8f9eb0); line-height: 1.5;
        margin: 0 0 28px;
      }
      .pw-actions {
        display: flex; justify-content: center; gap: 12px;
      }
      .pw-actions .pw-btn { text-decoration: none; }
    `}</style>
  )
}
